import { useEffect, useMemo, useState } from "react";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { getRFQs } from "../../../../api/client";
import { queryKeys } from "../../../../api/queryKeys";
import { useDebounce } from "../../../../hooks/useDebounce";
import type { RFQ } from "../../../../types/rfq";
import type { TeamMember } from "../../../../types";
import type { ErrorLike } from "../../../../utils/errors";
import type { RFQTab } from "../TabNavigation";

/** The tab's RFQs that match the user (DIY) and the search text, as typed. */
function filterRfqs(rfqs: RFQ[], activeTab: RFQTab, searchKeyword: string, selectedUser: TeamMember | null): RFQ[] {
  return rfqs.filter((rfq) => {
    // Data is already filtered by status from the database query
    if (rfq.status !== activeTab) {
      console.warn(`Unexpected: RFQ ${rfq.rfq_id} has status ${rfq.status} but tab is ${activeTab}`);
      return false;
    }

    // Filter by selected user for DIY tab
    if (activeTab === "DIY" && selectedUser) {
      if (rfq.moved_to_manual_by !== selectedUser.name) {
        return false;
      }
    }

    // Client-side search (a search fetches more records - 1500 - to search through)
    if (searchKeyword.trim()) {
      const keyword = searchKeyword.toLowerCase().trim();

      if (rfq.client_name?.toLowerCase().includes(keyword) ||
          rfq.client_email?.toLowerCase().includes(keyword)) {
        return true;
      }

      return rfq.items?.some(
        (item) =>
          item.item_name?.toLowerCase().includes(keyword) ||
          item.model_number?.toLowerCase().includes(keyword)
      );
    }

    return true;
  });
}

/**
 * The RFQ list's data: the active tab's RFQs from the server (the search is
 * sent once typing pauses for 500 ms), filtered on the client by the search
 * as typed and the DIY user, newest first. Refetches whenever the selected
 * user changes, and on mount.
 */
export function useRfqListQuery(activeTab: RFQTab, searchKeyword: string, selectedUser: TeamMember | null) {
  const [errorMessage, setErrorMessage] = useState("");
  // Debounce search keyword to avoid excessive API calls while typing
  const debouncedSearchKeyword = useDebounce(searchKeyword, 500);

  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: queryKeys.rfqs.list(activeTab, debouncedSearchKeyword),
    queryFn: async () => {
      try {
        // When searching the server returns more records (1500) to search
        // through; otherwise a limited set (500 for QUOTED).
        const results = await getRFQs({
          status: activeTab,
          searchQuery: debouncedSearchKeyword.trim() || undefined
        });
        setErrorMessage("");
        return results;
      } catch (caught) {
        const err = caught as ErrorLike;
        console.error("Error fetching RFQs:", err);

        if (err.message && (err.message.includes('timeout') || err.message.includes('statement_timeout'))) {
          setErrorMessage(`Database query timed out. Showing cached data. Please try refreshing in a moment.`);
        } else {
          setErrorMessage(`Failed to fetch RFQs: ${err.message || "Unknown error"}`);
        }

        // Rethrow, not return []: an empty result replaced the cached list
        // with nothing. Thrown, React Query retries and keeps the last good
        // data on screen, with the banner above saying why.
        throw err;
      }
    },
    staleTime: 60000, // 1 minute
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 10000),
    // Keep previous data while refetching to prevent UI from clearing
    placeholderData: keepPreviousData,
    gcTime: 300000, // 5 minutes
    enabled: true,
  });

  // Refetch when the selected user changes, and on mount
  useEffect(() => {
    refetch();
  }, [selectedUser, refetch]);

  const rfqs = Array.isArray(data) ? data : [];

  const filteredRFQs = useMemo(
    () => filterRfqs(rfqs, activeTab, searchKeyword, selectedUser),
    [rfqs, activeTab, searchKeyword, selectedUser],
  );

  // Always the latest email first
  const sortedRFQs = useMemo(
    () =>
      [...filteredRFQs].sort((a: RFQ, b: RFQ) =>
        new Date(b.rfq_received_date || 0).getTime() - new Date(a.rfq_received_date || 0).getTime()
      ),
    [filteredRFQs],
  );

  return { data, isLoading, error, isFetching, errorMessage, setErrorMessage, filteredRFQs, sortedRFQs };
}
